import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  FormGroup,
  FormControl,
} from '@angular/forms';
import {
  faPlus,
  faEye,
  faTrash,
  faCalendar,
} from '@fortawesome/free-solid-svg-icons';
import {
  ConfirmDialogComponent,
  ConfirmDialogSettings,
} from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CashService } from 'src/app/services/cash.service';
import { ReturnsService } from 'src/app/services/returns.service';
import { ToastService } from 'src/app/services/toast.service';
import { ReturnAdditionalForAdd } from 'src/app/core/models/ReturnAdditional';
import { PartialLiquidationService } from '../../../services/partial-liquidation.service';
import { InstallmentService } from 'src/app/services/installment.service';
import { IPaginatedServiceParamters } from 'src/app/core/models/ITable';

// Definición de la interfaz original de Installment (para el objeto anidado)
interface Installment {
  id: number;
  number: number;
  amount: number;
  amountPaid: number;
  installmentName: string;
  debt: number | null;
  fromDate: string;
  endDate: string;
  expirationDate: string;
  details: string;
  installmentsQuantity: number;
  expirationDay: number;
  expirationIncrease: number;
  paymentId: number;
  agencyId: number;
  feeId: number | null;
  description: string;
  hasInVoice: boolean;
  restriction: string | null;
  contractId: number;
  surcharge: number | null;
  finalAmount: number;
  finalAmountToPay: number;
  installmentTypeId: number;
  installmentGenerationTypeId: number;
  installmentStatusId: number;
  additionalCharges: any[]; // Puedes refinar esto con una interfaz si es necesario
  returned?: number; // Aunque está en el nivel superior en la respuesta del API, lo mantuvimos aquí en la interfaz original
}

// Nueva interfaz para la estructura de datos que viene del API WithReturns
interface InstallmentWithReturnInfo {
  installment: Installment; // El objeto de cuota está anidado aquí
  displayOptions: any; // Define una interfaz más específica si es necesario
  pendingReturnsAmount: number;
  additionalChargesAmount: number;
  feeAmount: number;
  installmentRequirementForList: any[]; // Define una interfaz más específica si es necesario
  pendingFee: any[]; // Define una interfaz más específica si es necesario
  pendingReturn: any; // Define una interfaz más específica si es necesario
  returnIds: number[];
  returned?: number; // Propiedad para el estado de liquidación al dueño
  paymentId: number | null; // Este paymentId parece ser de nivel superior, no de la cuota anidada
  totalAmountPayed: number; // Este total también parece ser de nivel superior
  // Propiedades que agregamos en el frontend para la selección y modificación
  modType?: boolean | null;
  modAmount?: number | null;
}

@Component({
  selector: 'app-returns-form',
  templateUrl: './returns-form.component.html',
  styleUrls: ['./returns-form.component.css'],
})
export class ReturnsFormComponent implements OnInit {
  iconPlus = faPlus;
  iconSee = faEye;
  iconTrash = faTrash;
  iconCalendar = faCalendar;

  confirmDeleteBoxTitles: ConfirmDialogSettings;

  returnAdditionals: ReturnAdditionalForAdd[] = [];
  displayForm: boolean = false;
  submitted: boolean = false;

  @Input() edit?: boolean = false;
  @Input() editData?: any;
  @Input() createInMemory?: any;
  @Input() createInMemoryArray?: any;
  @Input() returnData?: any;

  @Output() cancelAction = new EventEmitter();
  @Output() closeAction = new EventEmitter();
  @Output() loadingScreen = new EventEmitter();

  @ViewChildren(ConfirmDialogComponent)
  confirmBox: QueryList<ConfirmDialogComponent>;
  @ViewChild('noCashForm') noCashForm: ElementRef;

  returnsFormGroup = new UntypedFormGroup({
    withdrawer: new UntypedFormControl('', Validators.required),
    isTransfer: new UntypedFormControl(false, Validators.required),
    description: new UntypedFormControl(''),
  });

  inFavor = false;
  returnDate;

  additionalFormGroup = new FormGroup({
    returnAdditionalSign: new FormControl<boolean>(false, Validators.required),
    returnAdditionalAmount: new FormControl<number>(null, [
      Validators.pattern('^[0-9]*$'),
      Validators.min(0),
      Validators.max(2147483647),
      Validators.required,
    ]),
    returnAdditionalDescription: new FormControl<string>(null, [
      Validators.required,
      Validators.minLength(1),
    ]),
  });

  installmentsDisponibles: InstallmentWithReturnInfo[] = [];
  selectedInstallments: number[] = [];
  totalAmount: number = 0;
  loadingInstallments: boolean = false;

  constructor(
    private returnService: ReturnsService,
    public modalService: NgbModal,
    private toastService: ToastService,
    private partialLiquidationService: PartialLiquidationService,
    private installmentService: InstallmentService
  ) {
    this.confirmDeleteBoxTitles = {
      title: '',
      subtitle: ``,
    };
  }

  ngOnInit() {
    if (this.returnData.ownerAmountInFavor) {
      this.inFavor = true;
    }
    // Inicializar el withrdraw si hay propietarios disponibles
    if (this.returnData.owners && this.returnData.owners.length > 0) {
      this.returnsFormGroup.patchValue({
        withdrawer: this.returnData.owners[0]
      });
    }
    this.loadInstallments();
  }

  loadInstallments() {
    this.selectedInstallments = []; // Limpiar selecciones anteriores
    this.installmentsDisponibles = []; // Limpiar cuotas anteriores mientras carga
    this.loadingInstallments = true; // Indicar que la carga ha iniciado

    const paginatedArguments: IPaginatedServiceParamters = {
      pageNumber: 1,
      pageSize: 100,
      period: 1,
      query: ''
    };

    this.installmentService.GetPaginatedInstallmentsWithReturns(
      this.returnData.contractId,
      paginatedArguments,
      false
    ).subscribe({
      next: (response: any) => {
        // Asegurarse de que response.result.installments es un array y contiene la estructura esperada
        if (response && response.result && Array.isArray(response.result.installments)) {
          this.installmentsDisponibles = response.result.installments.filter((i: InstallmentWithReturnInfo) => {
            // Solo mostrar cuotas que:
            // 1. Tienen la estructura anidada 'installment'
            // 2. No han sido liquidadas al dueño (returned == 3) y amountPaid > 0 y no tienen returnIds
             return i.installment && i.installment.amountPaid > 0 && (!i.returned || i.returned == 3 && i.returnIds.length == 0);
          });
          console.log('Cuotas disponibles después de cargar y filtrar:', this.installmentsDisponibles);
          this.calculateTotalAmount(); // Recalcular el total inicial
        } else {
          console.error('La respuesta del API no tiene la estructura esperada:', response);
          this.installmentsDisponibles = []; // Asegurar que el array esté vacío en caso de error
        }
        this.loadingInstallments = false; // Indicar que la carga ha finalizado (éxito)
      },
      error: (error) => {
        console.error('Error al cargar las cuotas:', error);
        this.installmentsDisponibles = []; // Asegurar que el array esté vacío en caso de error
        this.loadingInstallments = false; // Indicar que la carga ha finalizado (error)
      }
    });
  }

  toggleInstallment(installmentId: number): void {
    console.log('Toggle Installment llamado con ID:', installmentId);
    console.log('Installments Disponibles:', this.installmentsDisponibles);

    // Asegurarse de que el installmentId es un número válido
    if (typeof installmentId !== 'number' || isNaN(installmentId)) {
      console.error('Intento de seleccionar cuota con ID inválido:', installmentId);
      return; // No hacer nada si el ID no es válido
    }

    const index = this.selectedInstallments.indexOf(installmentId);
    if (index === -1) {
      // Agregar esta cuota específica
      this.selectedInstallments.push(installmentId);
      // Buscar la cuota por su ID anidado en installmentsDisponibles
      const cuota = this.installmentsDisponibles.find(item => item.installment && item.installment.id === installmentId);
      if (cuota) {
        // Modificar el objeto encontrado en el array
        cuota.modType = null;
        cuota.modAmount = null;
      } else {
        // Log si la cuota no se encuentra en el array (posible si el array cambió o el ID es incorrecto)
        console.error('Cuota no encontrada en installmentsDisponibles con ID:', installmentId);
      }
    } else {
      // Remover esta cuota específica
      this.selectedInstallments.splice(index, 1);
    }
    this.calculateTotalAmount(); // Recalcular el total al cambiar la selección
  }

  toggleAllInstallments() {
    if (this.selectedInstallments.length === this.installmentsDisponibles.length) {
      this.selectedInstallments = []; // Deseleccionar todo
      this.totalAmount = 0;
    } else {
      // Seleccionar todos los IDs válidos de las cuotas disponibles
      this.selectedInstallments = this.installmentsDisponibles
        .filter(item => item.installment && typeof item.installment.id === 'number' && !isNaN(item.installment.id)) // Filtrar objetos sin installment o con ID no numérico/NaN
        .map(item => item.installment.id);
      // Recalcular el total para todas las cuotas seleccionadas
      this.calculateTotalAmount();
    }
  }

  calculateTotalAmount() {
    this.totalAmount = this.installmentsDisponibles
      .filter(item => item.installment && this.selectedInstallments.includes(item.installment.id)) // Filtrar cuotas seleccionadas y con estructura anidada
      .reduce((total, item) => {
        // Usar item.installment.amount para el cálculo
        const amount = item.installment.amount;
        // Usar item.modAmount ya que modType y modAmount se agregan al objeto de nivel superior
        const modAmount = item.modAmount || 0;

        if (item.modType === null || item.modType === undefined) {
          return total + amount;
        } else if (item.modType === true) {
          return total + (amount + modAmount);
        } else { // modType === false (descuento)
          return total + (amount - modAmount);
        }
      }, 0);
  }

  submitPartialLiquidation() {

    if (this.selectedInstallments.length === 0) {
      console.warn('No hay cuotas seleccionadas para liquidación parcial.');
      return; // No hacer nada si no hay cuotas seleccionadas
    }

    // Validar el formulario antes de continuar
    if (this.returnsFormGroup.invalid) {
      console.error('Formulario de liquidación inválido. Verifique los campos.', this.returnsFormGroup.errors);
      this.returnsFormGroup.markAllAsTouched(); // Marcar campos para mostrar errores visualmente
      return;
    }

    // Verificación adicional y más explícita para el withdrawer
    const withdrawer = this.returnsFormGroup.value.withdrawer;
    
    if (withdrawer === null) {
        console.error('El propietario (withdrawer) es NULL.', withdrawer);
        // Opcional: mostrar un mensaje al usuario
        // this.toastService.showToast('error', 'Por favor, seleccione el propietario que retira.');
        return;
    }

    if (typeof withdrawer === 'undefined') {
         console.error('El propietario (withdrawer) es UNDEFINED.', withdrawer);
         // Opcional: mostrar un mensaje al usuario
        // this.toastService.showToast('error', 'Por favor, seleccione el propietario que retira.');
        return;
    }

    if (typeof withdrawer.id === 'undefined') {
         console.error('El propietario (withdrawer) no tiene una propiedad ID válida.', withdrawer);
         // Opcional: mostrar un mensaje al usuario
        // this.toastService.showToast('error', 'Por favor, seleccione el propietario que retira.');
        return;
    }

    this.submitted = true;

    console.log('Iniciando construcción de modifiers. Array a filtrar:', this.installmentsDisponibles);

    // Construir array de modificadores solo para cuotas seleccionadas con Descuento/Aumento y monto > 0
    const modifiers = this.installmentsDisponibles
      .filter(item => 
        item.installment && // Asegurar que item.installment existe
        this.selectedInstallments.includes(item.installment.id) && // Asegurar que la cuota está seleccionada
        (item.modType === true || item.modType === false) && // Asegurar que modType es Aumento o Descuento
        (item.modAmount || 0) > 0 // Asegurar que modAmount es mayor a 0
      )
      .map(item => {
        // En este punto, item.installment, item.installment.id, modType y modAmount > 0 están validados
        const id = item.installment.id;
        const number = item.installment.number; 

        // Validación adicional solo para number si es necesario para la descripción
        if (typeof number !== 'number' || isNaN(number)) {
           console.warn('Número de cuota inválido en item.installment para descripción:', item);
        }

        return {
          installmentId: id,
          amount: item.modAmount,
          description: `Modificador cuota #${number || 'desconocido'}`,
          isAddition: item.modType
        };
      });

    console.log('Modifiers construidos:', modifiers);

    const liquidation = {
      installmentIds: this.selectedInstallments,
      ownerId: withdrawer.id,
      modifiers: modifiers,
      details: this.returnsFormGroup.value.description == "" ?  `PAGO GENERADO ${new Date().toLocaleDateString()}` :  this.returnsFormGroup.value.description,
    };
    console.log('Datos de liquidación a enviar:', liquidation);
    this.partialLiquidationService.processPartialLiquidation(liquidation)
      .subscribe({
        next: () => {
          this.submitted = false;
          this.cancel();
        },
        error: () => {
          this.submitted = false;
        }
      });
  }

  submitReturn() {
    const amountString = (
      this.returnData.amount +
      (this.inFavor ? this.returnData.ownerAmountInFavor : 0)
    ).toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    console.log( this.returnsFormGroup.value.description,"log");
    
    let returnToSubmit = {
      detail: this.returnsFormGroup.value.description == "" ?  `PAGO GENERADO ${new Date().toLocaleDateString()}` :  this.returnsFormGroup.value.description,
      contractId: this.returnData.contractId,
      isTransfer: this.returnsFormGroup.value.isTransfer,
      withdrawer: this.returnsFormGroup.value.withdrawer.id,
      returnAdditionals: this.returnAdditionals,
      inFavor: this.inFavor,
      installmentId:
        this.returnData.installmentId > 0 ? this.returnData.installmentId : 0,
      date: this.returnDate
        ? new Date(
            this.returnDate.year,
            this.returnDate.month - 1,
            this.returnDate.day,
          ).toISOString()
        : null,
    };

    this.confirmDeleteBoxTitles = {
      title: 'Liquidaciones',
      subtitle: `Desea crear esta Liquidación por $${amountString}?`,
    };

    if (this.returnsFormGroup.invalid) {
      this.returnsFormGroup.markAllAsTouched();
      return;
    } else {
      if (!returnToSubmit.isTransfer) {
        // this.cashService.getAllCashRegisters().subscribe((response: any) => {
        this.confirmBox
          .toArray()[0]
          .openModal()
          .result.then(
            (result) => {
              this.loadingScreen.emit(true);
              if (result) {
                this.submitted = true;
                this.returnService.createReturn(returnToSubmit).subscribe(
                  (response) => {
                    this.toastService.showToast(
                      'success',
                      'Liquidación creada',
                    );
                    this.closeAction.emit(response);
                  },
                  (error) => {
                    this.loadingScreen.emit(false);
                    this.toastService.showToast('error', 'Hubo un problema');
                  },
                );
              } else {
                this.loadingScreen.emit(false);
              }
            },
            (reason) => {
              this.loadingScreen.emit(true);
            },
          );
      } else {
        this.confirmBox
          .toArray()[0]
          .openModal()
          .result.then(
            (result) => {
              this.loadingScreen.emit(true);
              if (result) {
                this.returnService
                  .createReturn(returnToSubmit)
                  .subscribe((response) => {
                    this.toastService.showToast(
                      'success',
                      'Liquidación creada',
                    );
                    this.closeAction.emit(response);
                  });
              } else {
                this.loadingScreen.emit(false);
              }
            },
            (reason) => {
              this.loadingScreen.emit(true);
              //
            },
          );
      }
    }
  }

  validate(field: string) {
    return (
      this.returnsFormGroup.controls[field].errors &&
      this.returnsFormGroup.controls[field].touched
    );
  }

  ok() {
    this.modalService.dismissAll();
  }

  cancel() {
    this.cancelAction.emit();
  }

  createAdditional() {
    if (this.additionalFormGroup.invalid) {
      this.additionalFormGroup.markAllAsTouched();
      return;
    } else {
      let additional: ReturnAdditionalForAdd = {
        sign: this.additionalFormGroup.value.returnAdditionalSign,
        amount: this.additionalFormGroup.value.returnAdditionalAmount,
        detail: this.additionalFormGroup.value.returnAdditionalDescription,
      };
      this.returnAdditionals.push(additional);

      this.additionalFormGroup.markAsUntouched();
      this.additionalFormGroup.patchValue({
        returnAdditionalAmount: null,
        returnAdditionalDescription: null,
      });
    }
  }

  deleteAdditional(index) {
    this.returnAdditionals.splice(index, 1);
  }
}
